import { useState, useCallback } from 'react';
import { apiService } from '../services/api.ts';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { isAuthSessionExpiredError } from '../services/authService.tsx';

export const useFolders = (token) => {
  const { t } = useTranslation();
  const [folders, setFolders] = useState([]);

  const fetchFolders = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await apiService.getFolders(token);
      setFolders(response);
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_fetching_folders'), { position: "bottom-left" });
    }
  }, [token, t]);

  const createFolder = useCallback(async (folderData) => {
    if (!token) return;
    
    try {
      const response = await apiService.createFolder(token, folderData);
      setFolders(prev => [...prev, response]);
      toast.success(t('folder_created_successfully'), { position: "bottom-left" });
      return response;
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_creating_folder'), { position: "bottom-left" });
    }
  }, [token, t]);

  const updateFolder = useCallback(async (folderId, folderData) => {
    if (!token) return;
    
    try {
      await apiService.updateFolder(token, folderId, folderData);
      setFolders(prev => prev.map(folder => 
        folder.folder_id === folderId ? { ...folder, ...folderData } : folder
      ));
      toast.success(t('folder_name_updated'), { position: "bottom-left" });
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_renaming_folder'), { position: "bottom-left" });
    }
  }, [token, t]);

  const deleteFolder = useCallback(async (folderId) => {
    if (!token) return;
    
    try {
      await apiService.deleteFolder(token, folderId);
      setFolders(prev => prev.filter(folder => folder.folder_id !== folderId));
      toast.success(t('folder_deleted'), { position: "bottom-left" });
    } catch (error) {
      if (isAuthSessionExpiredError(error)) return;
      toast.error(t('error_deleting_folder'), { position: "bottom-left" });
    }
  }, [token, t]);

  return {
    folders,
    setFolders,
    fetchFolders,
    createFolder,
    updateFolder,
    deleteFolder
  };
};
